import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/useMediaQuery"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "./drawer"

type MaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full"

const maxWidthClasses: Record<MaxWidth, string> = {
  xs: "sm:max-w-[340px]",
  sm: "sm:max-w-[425px]",
  md: "sm:max-w-[500px]",
  lg: "sm:max-w-[600px]",
  xl: "sm:max-w-[700px]",
  "2xl": "sm:max-w-[800px]",
  "3xl": "sm:max-w-[900px]",
  "4xl": "sm:max-w-[1000px]",
  "5xl": "sm:max-w-[1140px]",
  "6xl": "sm:max-w-[1280px]",
  "full": "sm:max-w-[calc(100vw-4rem)]",
}

interface ResponsiveModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

interface ResponsiveModalContentProps {
  children: React.ReactNode
  className?: string
  maxWidth?: MaxWidth
}

interface ResponsiveModalHeaderProps {
  children: React.ReactNode
  className?: string
}

interface ResponsiveModalFooterProps {
  children: React.ReactNode
  className?: string
}

interface ResponsiveModalTitleProps {
  children: React.ReactNode
  className?: string
}

interface ResponsiveModalDescriptionProps {
  children: React.ReactNode
  className?: string
}

interface ResponsiveModalBodyProps {
  children: React.ReactNode
  className?: string
}

// Context to share mobile state with child components
const ResponsiveModalContext = React.createContext<{ isMobile: boolean }>({
  isMobile: false,
})

const useResponsiveModalContext = () => React.useContext(ResponsiveModalContext)

const ResponsiveModal = ({ open, onOpenChange, children }: ResponsiveModalProps) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <ResponsiveModalContext.Provider value={{ isMobile }}>
        <Drawer open={open} onOpenChange={onOpenChange}>
          {children}
        </Drawer>
      </ResponsiveModalContext.Provider>
    )
  }

  return (
    <ResponsiveModalContext.Provider value={{ isMobile }}>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        {children}
      </DialogPrimitive.Root>
    </ResponsiveModalContext.Provider>
  )
}

const ResponsiveModalContent = ({
  children,
  className,
  maxWidth = "lg",
}: ResponsiveModalContentProps) => {
  const { isMobile } = useResponsiveModalContext()

  if (isMobile) {
    return (
      <DrawerContent className={cn("max-h-[90vh]", className)}>
        <div className="flex flex-col overflow-hidden max-h-[calc(90vh-2rem)]">
          {children}
        </div>
      </DrawerContent>
    )
  }

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-[50%] top-[50%] z-50 flex flex-col w-full translate-x-[-50%] translate-y-[-50%] border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[85vh] overflow-hidden",
          maxWidthClasses[maxWidth],
          className
        )}
      >
        <div className="flex flex-col flex-1 overflow-hidden [&>form]:flex [&>form]:flex-col [&>form]:flex-1 [&>form]:overflow-hidden">
          {children}
        </div>
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

const ResponsiveModalHeader = ({ children, className }: ResponsiveModalHeaderProps) => {
  const { isMobile } = useResponsiveModalContext()

  if (isMobile) {
    return (
      <DrawerHeader className={cn("shrink-0 border-b px-4 pb-4 text-left", className)}>
        {children}
      </DrawerHeader>
    )
  }

  return (
    <div className={cn("shrink-0 border-b px-6 py-4 flex flex-col space-y-1.5", className)}>
      {children}
    </div>
  )
}

const ResponsiveModalBody = ({ children, className }: ResponsiveModalBodyProps) => {
  return (
    <div className={cn("flex-1 overflow-y-auto px-6 py-4", className)}>
      {children}
    </div>
  )
}

const ResponsiveModalFooter = ({ children, className }: ResponsiveModalFooterProps) => {
  const { isMobile } = useResponsiveModalContext()

  if (isMobile) {
    return (
      <DrawerFooter className={cn("shrink-0 border-t px-4 pt-4 flex-row justify-end gap-2", className)}>
        {children}
      </DrawerFooter>
    )
  }

  return (
    <div className={cn("shrink-0 border-t px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}>
      {children}
    </div>
  )
}

const ResponsiveModalTitle = ({ children, className }: ResponsiveModalTitleProps) => {
  const { isMobile } = useResponsiveModalContext()

  if (isMobile) {
    return <DrawerTitle className={className}>{children}</DrawerTitle>
  }

  return (
    <DialogPrimitive.Title className={cn("text-lg font-semibold leading-none tracking-tight", className)}>
      {children}
    </DialogPrimitive.Title>
  )
}

const ResponsiveModalDescription = ({ children, className }: ResponsiveModalDescriptionProps) => {
  const { isMobile } = useResponsiveModalContext()

  if (isMobile) {
    return <DrawerDescription className={className}>{children}</DrawerDescription>
  }

  return (
    <DialogPrimitive.Description className={cn("text-sm text-muted-foreground", className)}>
      {children}
    </DialogPrimitive.Description>
  )
}

const ResponsiveModalClose = ({ children, className, asChild }: { children?: React.ReactNode; className?: string; asChild?: boolean }) => {
  const { isMobile } = useResponsiveModalContext()

  if (isMobile) {
    return <DrawerClose className={className} asChild={asChild}>{children}</DrawerClose>
  }

  return <DialogPrimitive.Close className={className} asChild={asChild}>{children}</DialogPrimitive.Close>
}

export {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalClose,
}
